
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { Button, Input, Card } from '../components/UI';
import { Shield, Globe, Lock, User, Mail, ArrowRight } from '../components/Icons';
import { useTranslation } from '../contexts/LanguageContext';

const Signup: React.FC = () => {
  const { t, language, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate signup and proceed to onboarding
    setTimeout(() => {
      setIsLoading(false);
      navigate('/onboarding');
    }, 1500);
  };

  return (
    <div className="min-h-screen w-full bg-[#F4F4F4] dark:bg-[#050505] flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-[#474D22] opacity-5 blur-[120px] rounded-full pointer-events-none"></div>
      
      <div className="absolute top-0 right-0 p-8 z-10">
        <button 
          onClick={() => setLanguage(language === 'en' ? 'de' : 'en')}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#161616] border border-gray-200 dark:border-gray-800 text-[10px] font-bold uppercase tracking-wider text-[#474D22] dark:text-white hover:border-[#474D22] transition-colors shadow-sm"
        >
          <Globe size={14} />
          {language === 'en' ? 'Deutsch' : 'English'}
        </button>
      </div>

      <div className="w-full max-w-[440px] z-10">
        <div className="flex flex-col items-center mb-10 text-center">
          <div className="w-16 h-16 bg-[#474D22] flex items-center justify-center mb-6 shadow-xl">
             <BrandLogo type="main" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-[#161616] dark:text-white tracking-tight uppercase">
             Create VMI Account
          </h1>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-[0.2em] mt-3">Vendor Inventory Registration</p>
        </div>

        <Card className="p-10 border-t-4 border-t-[#474D22] shadow-2xl bg-white dark:bg-[#111111]">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Full Name</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><User size={16} /></div>
                <Input type="text" placeholder="John Doe" required className="pl-10 h-11" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Work Email Address</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Mail size={16} /></div>
                <Input type="email" placeholder="name@company.com" required className="pl-10 h-11" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Password</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Lock size={16} /></div>
                <Input type="password" placeholder="••••••••" required className="pl-10 h-11" />
              </div>
              <p className="text-[9px] text-gray-400 font-medium">Minimum 12 characters with symbols.</p>
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full h-12 text-xs font-bold tracking-[0.15em] shadow-lg"
                disabled={isLoading}
              >
                {isLoading ? 'CREATING ACCOUNT...' : 'START ONBOARDING'}
                {!isLoading && <ArrowRight size={14} className="ml-2" />}
              </Button>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800 text-center">
            <p className="text-xs text-gray-500 font-medium">
              Already have an enterprise account? <Link to="/login" className="text-[#474D22] dark:text-[#717C26] font-bold underline underline-offset-4">Sign In</Link>
            </p>
          </div>
        </Card>

        <div className="mt-8 flex items-center justify-center gap-2 text-gray-400 dark:text-gray-600 opacity-60">
           <Shield size={12} />
           <span className="text-[10px] font-bold uppercase tracking-widest">ISO 27001 Certified Platform</span>
        </div>
      </div>
    </div>
  );
};

export default Signup;
